import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Reveal, SectionTag } from "./Reveal";

export default function StudyMap() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    try {
      const map = L.map(containerRef.current, {
        scrollWheelZoom: false,
        zoomControl: true,
        attributionControl: true,
        minZoom: 3,
        maxZoom: 9,
      }).setView([-63.4, -59.5], 4);
      mapRef.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }).addTo(map);

      const region = L.polygon(
        [
          [-61.8, -55.5],
          [-61.8, -63.5],
          [-66.0, -66.5],
          [-66.0, -57.5],
        ],
        { color: "#E53945", weight: 1.5, dashArray: "6 6", fillColor: "#E53945", fillOpacity: 0.08 }
      ).addTo(map);
      region.bindPopup("PROPOSED STUDY REGION — Antarctic Peninsula (to be defined with scientific collaborators)");

      L.circleMarker([-62.2, -58.9], {
        radius: 6,
        color: "#9DDCF4",
        weight: 1.5,
        fillColor: "#9DDCF4",
        fillOpacity: 0.6,
      })
        .addTo(map)
        .bindPopup("KING GEORGE ISLAND — international research station hub (reference point)");

      L.circleMarker([-63.4, -57.0], {
        radius: 6,
        color: "#D4AF37",
        weight: 1.5,
        fillColor: "#D4AF37",
        fillOpacity: 0.6,
      })
        .addTo(map)
        .bindPopup("ANTARCTIC PENINSULA — candidate observation environment (reference point)");

      return () => {
        map.remove();
        mapRef.current = null;
      };
    } catch (err) {
      setFailed(true);
      return undefined;
    }
  }, []);

  return (
    <section id="study-region" data-testid="study-region-section" className="relative py-24 lg:py-32">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <SectionTag index="06" label="Proposed Study Region" />
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-ice leading-[0.95] max-w-3xl">
              The Antarctic <span className="text-polar">Peninsula</span>
            </h2>
            <p className="max-w-md text-sm text-ice/60 leading-relaxed lg:text-right">
              A candidate region for the proposed programme. The final study region will be defined with a qualified
              scientific collaborator.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-14 relative border border-polar/20 clip-corner overflow-hidden" data-testid="map-container">
            {failed ? (
              <div className="h-[420px] sm:h-[520px] bg-[#0B1F35] flex flex-col items-center justify-center gap-4 p-8 text-center">
                <div className="font-mono text-xs tracking-[0.25em] text-mission">MAP UNAVAILABLE</div>
                <div className="font-mono text-xs text-polar tracking-widest">63°S 059°W — ANTARCTIC PENINSULA</div>
                <p className="text-sm text-ice/60 max-w-sm">
                  The interactive map could not load. The proposed study region is the Antarctic Peninsula; the final
                  region will be defined with scientific collaborators.
                </p>
              </div>
            ) : (
              <div ref={containerRef} className="h-[420px] sm:h-[520px] w-full" role="application" aria-label="Interactive map of the Antarctic Peninsula proposed study region" />
            )}
            <div className="absolute bottom-4 left-4 z-[500] bg-abyss/85 backdrop-blur-sm border border-polar/20 px-4 py-3 font-mono text-[10px] tracking-[0.15em] text-ice/80 space-y-1.5 pointer-events-none">
              <div className="flex items-center gap-2"><span className="w-3 h-3 border border-mission inline-block" style={{ background: "rgba(229,57,69,0.15)" }} /> PROPOSED STUDY REGION</div>
              <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-polar/70 inline-block" /> REFERENCE — RESEARCH HUB</div>
              <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-gold/70 inline-block" /> REFERENCE — PENINSULA</div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
